import { IToolPanelParams } from 'ag-grid-community';
import {
  Badge,
  Box,
  Button,
  Dialog,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import IconButton from 'components/@extended/IconButton';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, LoginOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { openSnackbar } from 'store/reducers/snackbar';
import { useDispatch } from 'react-redux';
import { useSavePreferencesMutation, useProfileQuery, GridType, UserGridPreferencesDto } from '../../graphql/types';
import AddGridPreferenceView from './AddGridPreferenceView';
import useAuth from '../../hooks/useAuth';
import { sharedCompactViewKey } from './McaTransactions';
import { mcaRecurrencyKeyName } from './McaRecurrency';
import { mcaFeeTransactionKeyName } from './McaFeesTransactions';

const defaultPageSize = 50;
export default (
  props: IToolPanelParams & {
    preferencesKey: GridType;
    keyName: string;
    sharedKeyName?: string;
    displayInfiniteScrollToggle?: boolean;
    displayCompactViewToggle?: boolean;
    refreshGrid: () => void;
  }
) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { preferencesKey, keyName, displayInfiniteScrollToggle = true, displayCompactViewToggle = false, refreshGrid, sharedKeyName } = props;
  const isScrollUsed = localStorage.getItem(keyName + '_USE_SCROLL') === 'false' ? null : true;
  const isCompactViewUsed = localStorage.getItem(keyName + '_USE_COMPACT_VIEW') === 'false' ? null : true;
  const [useScroll, setUseScroll] = useState<boolean>(isScrollUsed ?? false);
  const [useCompactView, setUseCompactView] = useState<boolean>(isCompactViewUsed ?? false);
  const [savePreferences] = useSavePreferencesMutation();
  const { data, loading, refetch } = useProfileQuery({ fetchPolicy: 'network-only' });
  const [add, setAdd] = useState<boolean>(false);
  const handleAdd = () => {
    setAdd(!add);
  };
  const { user: currentUser } = useAuth();
  const userId = currentUser?.id;
  const allPreferences = useMemo(() => data?.profile?.gridPreferences || [], [data]);
  const savedPreferences = useMemo(() => data?.profile?.gridPreferences?.filter((pref) => pref.key === preferencesKey) || [], [data]);

  const applyState = async (index: number) => {
    const gridPreferences = savedPreferences.map((sp, n) => {
      return { ...sp, active: n === index, __typename: undefined } as UserGridPreferencesDto;
    });
    const gridPreference = gridPreferences[index];
    const savedPreferencesMap = new Map(savedPreferences.map((sp) => [`${sp.key}-${sp.name}`, true]));
    const missingPreferences = allPreferences.filter((sp) => {
      return !savedPreferencesMap.has(`${sp.key}-${sp.name}`);
    });
    const updatedGridPreferences = [
      ...gridPreferences,
      ...missingPreferences.map((sp) => ({ ...sp, __typename: undefined }) as UserGridPreferencesDto)
    ];

    await savePreferences({
      variables: {
        dto: {
          gridPreferences: updatedGridPreferences
        }
      }
    });
    await refetch();
    localStorage.setItem(keyName + '_COLUMNS_STATE', gridPreference.columnsState);
    props.api.applyColumnState({ state: JSON.parse(localStorage.getItem(keyName + '_COLUMNS_STATE') || '[]') });
    localStorage.setItem(keyName + '_ROWS_PER_PAGE_STATE', (gridPreference.rowsPerPage || defaultPageSize) + '');
    props.api.updateGridOptions({ paginationPageSize: gridPreference.rowsPerPage || defaultPageSize });
    localStorage.setItem(keyName + '_FILTER_STATE', gridPreference.filterState);
    props.api.setFilterModel(JSON.parse(localStorage.getItem(keyName + '_FILTER_STATE') || '{}'));
    localStorage.setItem(keyName + '_USE_SCROLL', gridPreference.useScroll ? 'true' : 'false');
    if (gridPreference.useScroll !== useScroll) {
      navigate(0);
    }
    dispatch(
      openSnackbar({
        open: true,
        message: 'List state successfully applied.',
        variant: 'alert',
        alert: {
          color: 'success'
        },
        close: true
      })
    );
  };

  const deleteState = async (index: number) => {
    const gridPreferences = savedPreferences.map((sp) => {
      return { ...sp, __typename: undefined } as UserGridPreferencesDto;
    });
    gridPreferences.splice(index, 1);
    const savedPreferencesMap = new Map(savedPreferences.map((sp) => [`${sp.key}-${sp.name}`, true]));
    const missingPreferences = allPreferences.filter((sp) => {
      return !savedPreferencesMap.has(`${sp.key}-${sp.name}`);
    });
    const updatedGridPreferences = [
      ...gridPreferences,
      ...missingPreferences.map((sp) => ({ ...sp, __typename: undefined }) as UserGridPreferencesDto)
    ];
    await savePreferences({
      variables: {
        dto: {
          gridPreferences: updatedGridPreferences
        }
      }
    });
    await refetch();
    dispatch(
      openSnackbar({
        open: true,
        message: 'List state successfully deleted.',
        variant: 'alert',
        alert: {
          color: 'success'
        },
        close: true
      })
    );
  };

  const addNewState = async (stateName: string) => {
    const columnsState = JSON.stringify(props.api.getColumnState());
    const filterState = JSON.stringify(props.api.getFilterModel());
    const rowsPerPage = props.api.paginationGetPageSize();
    const gridPreferences = savedPreferences.map((sp) => {
      return { ...sp, active: false, __typename: undefined } as UserGridPreferencesDto;
    });
    const savedPreferencesMap = new Map(savedPreferences.map((sp) => [`${sp.key}-${sp.name}`, true]));
    const missingPreferences = allPreferences.filter((sp) => {
      return !savedPreferencesMap.has(`${sp.key}-${sp.name}`);
    });
    const updatedGridPreferences = [
      ...gridPreferences,
      ...missingPreferences.map((sp) => ({ ...sp, __typename: undefined }) as UserGridPreferencesDto)
    ];
    updatedGridPreferences.push({
      name: stateName,
      active: true,
      key: preferencesKey,
      keyName,
      useScroll,
      columnsState,
      filterState,
      rowsPerPage
    });
    await savePreferences({
      variables: {
        dto: {
          gridPreferences: updatedGridPreferences
        }
      }
    });
    localStorage.setItem(keyName + '_COLUMNS_STATE', columnsState);
    localStorage.setItem(keyName + '_FILTER_STATE', filterState);
    localStorage.setItem(keyName + '_ROWS_PER_PAGE_STATE', rowsPerPage + '');
    localStorage.setItem(keyName + '_USE_SCROLL', useScroll + '');
    await refetch();
    dispatch(
      openSnackbar({
        open: true,
        message: 'List state successfully saved.',
        variant: 'alert',
        alert: {
          color: 'success'
        },
        close: true
      })
    );
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box ml={2}>
      {displayInfiniteScrollToggle && (
        <Grid container spacing={2} pl={1} pb={2} pt={2}>
          <Grid item>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="secondary">
                Infinite Scroll
              </Typography>
              <Switch
                checked={useScroll}
                size="small"
                onChange={(event, checked) => {
                  setUseScroll(checked);
                  if (checked) {
                    localStorage.setItem(keyName + '_USE_SCROLL', 'true');
                  } else {
                    localStorage.setItem(keyName + '_USE_SCROLL', 'false');
                  }
                  if (checked !== isScrollUsed) {
                    navigate(0);
                  }
                }}
              />
            </Stack>
          </Grid>
        </Grid>
      )}

      {displayCompactViewToggle && (
        <Grid container spacing={2} pl={1} pb={2} pt={2}>
          <Grid item>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="secondary">
                Compact View
              </Typography>
              <Switch
                checked={useCompactView}
                size="small"
                onChange={(event, checked) => {
                  setUseCompactView(checked);
                  localStorage.setItem(keyName + '_USE_COMPACT_VIEW', checked ? 'true' : 'false');
                  localStorage.removeItem(keyName + '_COLUMNS_STATE');
                  if (sharedKeyName === sharedCompactViewKey && userId) {
                    const sharedKeys = [
                      `${userId}_${sharedCompactViewKey}`,
                      `${userId}_${mcaRecurrencyKeyName}`,
                      `${userId}_${mcaFeeTransactionKeyName}`
                    ];

                    sharedKeys.forEach((baseKey) => {
                      localStorage.setItem(baseKey + '_USE_COMPACT_VIEW', checked ? 'true' : 'false');
                      localStorage.removeItem(baseKey + '_COLUMNS_STATE');
                    });
                  }
                  refreshGrid();
                  props.api.autoSizeAllColumns(true);
                }}
              />
            </Stack>
          </Grid>
        </Grid>
      )}
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2} pl={1} pb={2}>
        <Grid item>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              sx={{ pl: 4, pr: 4 }}
              fullWidth
              variant="contained"
              startIcon={<SaveOutlined />}
              onClick={async (e: any) => {
                e.stopPropagation();
                handleAdd();
              }}
            >
              Save View
            </Button>
            <IconButton
              variant="outlined"
              color="secondary"
              onClick={(e: any) => {
                e.stopPropagation();
                localStorage.removeItem(keyName + '_COLUMNS_STATE');
                localStorage.removeItem(keyName + '_FILTER_STATE');
                localStorage.removeItem(keyName + '_ROWS_PER_PAGE_STATE');
                localStorage.removeItem(keyName + '_USE_SCROLL');
                if (sharedKeyName === sharedCompactViewKey && userId) {
                  const keysToReset = [
                    `${userId}_${mcaRecurrencyKeyName}`,
                    `${userId}_${mcaFeeTransactionKeyName}`,
                  ];

                  keysToReset.forEach((baseKey) => {
                    localStorage.removeItem(baseKey + '_USE_COMPACT_VIEW');
                    localStorage.removeItem(baseKey + '_COLUMNS_STATE');
                    localStorage.removeItem(baseKey + '_FILTER_STATE');
                    localStorage.removeItem(baseKey + '_ROWS_PER_PAGE_STATE');
                    localStorage.removeItem(baseKey + '_USE_SCROLL');
                  });
                }
                props.api.resetColumnState();
                props.api.setFilterModel({});
                props.api.updateGridOptions({ paginationPageSize: defaultPageSize });
                refreshGrid();
                props.api.autoSizeAllColumns(true);
                dispatch(
                  openSnackbar({
                    open: true,
                    message: 'List state successfully restored.',
                    variant: 'alert',
                    alert: {
                      color: 'success'
                    },
                    close: true
                  })
                );
              }}
            >
              <ReloadOutlined />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>

      <Divider />
      <Grid container spacing={2} pl={1} pt={2}>
        <Grid item>
          <Typography variant="caption" color="primary">
            Saved Views
          </Typography>
          <List dense={true} sx={{ width: '280px' }}>
            {savedPreferences.map((sp, i) => (
              <ListItem
                key={sp.name}
                divider
                secondaryAction={
                  <Stack spacing={1} direction="row">
                    <IconButton edge="end" aria-label="delete" color={'warning'} onClick={() => deleteState(i)}>
                      <DeleteOutlined />
                    </IconButton>
                    <IconButton edge="end" aria-label="load" onClick={() => applyState(i)}>
                      <LoginOutlined />
                    </IconButton>
                  </Stack>
                }
              >
                {sp.active && (
                  <Badge color="success" variant="dot">
                    <ListItemText primary={sp.name} />
                  </Badge>
                )}
                {!sp.active && <ListItemText primary={sp.name} />}
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>

      {/* add syndication dialog */}
      <Dialog maxWidth="sm" fullWidth onClose={handleAdd} open={add} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {add && <AddGridPreferenceView entity={null} onCancel={handleAdd} onSave={(stateName) => addNewState(stateName)} />}
      </Dialog>
    </Box>
  );
};
